/**
 * Every user-facing string rendered by @oskey/ui (visible text and accessible names), grouped by component.
 * Components read them through `OSK_UI_LABELS`; a component input with the same purpose
 * (e.g. `<osk-modal closeAriaLabel="…">`) still wins for that one instance.
 */
export interface OSKUiLabels {
  spinner: { loading: string };
  input: { clear: string; showPassword: string; hidePassword: string };
  codeDisplay: { show: string; hide: string; copy: string };
  timeInput: { hours: string; minutes: string };
  timeField: { label: string };
  select: { placeholder: string; searchPlaceholder: string; empty: string; clear: string; clearAll: string };
  searchField: { placeholder: string; empty: string; clear: string; allCategories: string };
  datePicker: {
    placeholder: string;
    time: string;
    clear: string;
    today: string;
    yesterday: string;
    startRole: string;
    endRole: string;
    startShort: string;
    endShort: string;
    rangeClickStart: string;
    rangeClickEnd: string;
  };
  pagination: { show: string; of: string; items: string; previousPage: string; nextPage: string };
  breadcrumb: { navigation: string };
  actionMenu: { trigger: string };
  alert: { close: string };
  emptyState: { title: string };
  modal: { dialog: string; close: string };
  drawer: { panel: string; close: string };
  confirmDialog: { title: string; message: string; confirm: string; cancel: string };
  dataTable: { empty: string; emptyChildren: string; expandRow: string };
  selectionList: { selectAll: string };
  toast: { close: string };
  appVersion: { ariaLabel: string };
}

/** Default labels: the French strings @oskey/ui has always rendered. */
export const OSK_UI_LABELS_FR: OSKUiLabels = {
  spinner: { loading: 'Chargement...' },
  input: { clear: 'Effacer', showPassword: 'Afficher le mot de passe', hidePassword: 'Masquer le mot de passe' },
  codeDisplay: { show: 'Afficher le code', hide: 'Masquer le code', copy: 'Copier le code' },
  timeInput: { hours: 'Heures', minutes: 'Minutes' },
  timeField: { label: 'Heure' },
  select: {
    placeholder: 'Sélectionner',
    searchPlaceholder: 'Filtrer...',
    empty: 'Aucun résultat trouvé',
    clear: 'Réinitialiser',
    clearAll: 'Tout désélectionner'
  },
  searchField: {
    placeholder: 'Rechercher...',
    empty: 'Aucune suggestion correspondante',
    clear: 'Effacer la recherche',
    allCategories: 'Tous'
  },
  datePicker: {
    placeholder: 'Sélectionner une date',
    time: 'Heure',
    clear: 'Effacer',
    today: 'Aujourd\'hui',
    yesterday: 'Hier',
    startRole: 'Sélection : Date de début',
    endRole: 'Sélection : Date de fin',
    startShort: 'Début',
    endShort: 'Fin',
    rangeClickStart: 'Cliquez sur la date de début',
    rangeClickEnd: 'Cliquez sur la date de fin'
  },
  pagination: {
    show: 'Montrer :',
    of: 'de',
    items: 'files',
    previousPage: 'Page précédente',
    nextPage: 'Page suivante'
  },
  breadcrumb: { navigation: 'Fil d\'Ariane' },
  actionMenu: { trigger: 'Actions' },
  alert: { close: 'Fermer l\'alerte' },
  emptyState: { title: 'Aucun élément trouvé' },
  modal: { dialog: 'Fenêtre modale', close: 'Fermer' },
  drawer: { panel: 'Panneau latéral', close: 'Fermer le panneau' },
  confirmDialog: {
    title: 'Confirmer l’action',
    message: 'Êtes-vous sûr de vouloir effectuer cette action ?',
    confirm: 'Confirmer',
    cancel: 'Annuler'
  },
  dataTable: {
    empty: 'Aucune donnée à afficher',
    emptyChildren: 'Aucun élément rattaché',
    expandRow: 'Déplier ou replier la ligne'
  },
  selectionList: { selectAll: 'Tout sélectionner' },
  toast: { close: 'Fermer la notification' },
  appVersion: { ariaLabel: 'Version de l’application' }
};

export const OSK_UI_LABELS_EN: OSKUiLabels = {
  spinner: { loading: 'Loading...' },
  input: { clear: 'Clear', showPassword: 'Show password', hidePassword: 'Hide password' },
  codeDisplay: { show: 'Show code', hide: 'Hide code', copy: 'Copy code' },
  timeInput: { hours: 'Hours', minutes: 'Minutes' },
  timeField: { label: 'Time' },
  select: {
    placeholder: 'Select',
    searchPlaceholder: 'Filter...',
    empty: 'No results found',
    clear: 'Reset',
    clearAll: 'Clear all'
  },
  searchField: {
    placeholder: 'Search...',
    empty: 'No matching suggestions',
    clear: 'Clear search',
    allCategories: 'All'
  },
  datePicker: {
    placeholder: 'Select a date',
    time: 'Time',
    clear: 'Clear',
    today: 'Today',
    yesterday: 'Yesterday',
    startRole: 'Selecting: start date',
    endRole: 'Selecting: end date',
    startShort: 'Start',
    endShort: 'End',
    rangeClickStart: 'Click the start date',
    rangeClickEnd: 'Click the end date'
  },
  pagination: { show: 'Show:', of: 'of', items: 'rows', previousPage: 'Previous page', nextPage: 'Next page' },
  breadcrumb: { navigation: 'Breadcrumb' },
  actionMenu: { trigger: 'Actions' },
  alert: { close: 'Close alert' },
  emptyState: { title: 'Nothing found' },
  modal: { dialog: 'Dialog', close: 'Close' },
  drawer: { panel: 'Side panel', close: 'Close panel' },
  confirmDialog: {
    title: 'Confirm action',
    message: 'Are you sure you want to do this?',
    confirm: 'Confirm',
    cancel: 'Cancel'
  },
  dataTable: { empty: 'No data to display', emptyChildren: 'No linked items', expandRow: 'Expand or collapse row' },
  selectionList: { selectAll: 'Select all' },
  toast: { close: 'Close notification' },
  appVersion: { ariaLabel: 'Application version' }
};

/** A partial labels object: any group, any key. Missing values come from the base set. */
export type OSKUiLabelsOverride = { [G in keyof OSKUiLabels]?: Partial<OSKUiLabels[G]> };

export function mergeOSKUiLabels(base: OSKUiLabels, override: OSKUiLabelsOverride | null | undefined): OSKUiLabels {
  if (!override) {
    return base;
  }
  const merged = { ...base } as Record<keyof OSKUiLabels, unknown>;
  for (const group of Object.keys(override) as (keyof OSKUiLabels)[]) {
    merged[group] = { ...base[group], ...override[group] };
  }
  return merged as OSKUiLabels;
}
